import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, TriangleAlert, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ESTADO_MSG, type Projection } from "../lib/plan";

/**
 * Aviso de espaço.
 *
 * ⚠️ Não aparece abaixo de 80%: o que foi vendido é "dispositivos ilimitados",
 * e um medidor permanente transforma a promessa em limite.
 *
 * A mensagem fala em DIAS DE HISTÓRICO, nunca em gigabytes: o cliente entende
 * dias, e dias é o que ele contratou.
 *
 * ⚠️ Aqui havia três botões de ampliação (+5 GB, +10 GB, +25 GB) que chamavam
 * `onAmpliar` — uma prop que NINGUÉM nunca passou. Clicar não fazia nada, e um
 * botão morto numa faixa de cobrança é pior que nenhum botão: o cliente conclui
 * que o sistema está quebrado justamente quando ele quer pagar mais. Agora é um
 * caminho só, e ele vai para a tela onde a decisão cabe — com o preço, o que
 * cada bloco resolve e quando entra na fatura.
 */
export function QuotaBanner({ p }: { p: Projection | null }) {
  const [fechado, setFechado] = useState(false);
  if (!p?.visible || fechado) return null;
  const msg = ESTADO_MSG[p.estado];
  if (!msg) return null;

  const grave = p.estado === "DEVICE_HOLD" || p.estado === "THROTTLE";
  const cor = grave ? "var(--color-serious)" : "var(--color-warning)";

  return (
    <AnimatePresence>
      <motion.div
        // Entrada anima posição, nunca opacidade: sem o quadro da animação,
        // a faixa ficaria invisível — já aconteceu três vezes neste projeto.
        initial={{ y: -8 }} animate={{ y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        className="rounded-[var(--radius-card)] border bg-white px-3 py-2 dark:bg-ink-900"
        style={{ borderColor: cor }}>
        <div className="flex items-start gap-3">
          <TriangleAlert size={16} strokeWidth={2.25} className="mt-0.5 shrink-0"
                         style={{ color: cor }} aria-hidden />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-[13px] font-semibold">{msg.titulo}</span>
            {p.estado !== "WARN" && (
              <span className="tnum text-xs text-ink-500 dark:text-ink-400">
                <strong className="text-ink-700 dark:text-ink-200">{p.dias} dias</strong> de {p.retencao_contratada}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-xs text-ink-600 dark:text-ink-300"
                  title={msg.texto}>{msg.texto}</span>
            {/* O rótulo diz o que a próxima tela FAZ, não para onde ela leva:
                "Espaço" seria um menu; "Ver opções de espaço" é uma promessa. */}
            <Link to="/espaco"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-[var(--radius-btn)]
                         px-2.5 py-1 text-xs font-semibold text-[var(--color-on-brand)] t-fast
                         hover:brightness-110"
              style={{ background: "var(--color-brand-tenant)" }}>
              Ver opções de espaço
              <ArrowRight size={13} aria-hidden />
            </Link>
          </div>
          <button onClick={() => setFechado(true)} aria-label="Fechar aviso"
            className="shrink-0 rounded p-1 text-ink-400 t-fast hover:bg-ink-100 dark:hover:bg-ink-800">
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
